// Pure, Angular-free, pouch-free helpers for classifying a failed food *search*.
// Kept out of food-db.service.ts so they can be unit-tested under node (that
// service transitively imports pouchdb-browser, which references `self` and
// cannot load in the test runner — same reason the sibling food-doc-read
// classifier lives in food-read-error.ts).
import { isTransientFetchError, isFirebaseAuthNetworkError, isOfflineOrTransientNetworkError } from './error-patterns';
import { isDatabaseRecovering, isDatabaseShuttingDown } from './database-unavailable-error';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';
import { BackendRouteUnresolvedError } from 'util/backend-route-gate';

/**
 * Thrown by the Elasticsearch food search (`runElasticQueryAsync` in
 * food-search-elastic.ts) when the search
 * request could NOT reach the server because of a transient connectivity
 * condition — an offline blip, an iOS WKWebView "Load failed", or the
 * NSURLErrorDomain "The network connection was lost." family. This is
 * deliberately distinct from an empty result set, which means "the search ran
 * and genuinely found nothing." Callers that would otherwise show a "couldn't
 * find what you're looking for" empty state catch this and instead show an
 * honest "couldn't load — connection problem" state with a retry, so a network
 * blip is never mistaken for "this food does not exist" (#3743).
 */
export class SearchConnectionError extends Error {
  readonly originalError: unknown;

  constructor(originalError: unknown) {
    const message = describeError(originalError);
    super(`Food search failed (connection): ${message}`);
    this.name = 'SearchConnectionError';
    this.originalError = originalError;
  }
}

/**
 * Thrown by the Elasticsearch food search (`runElasticQueryAsync` in
 * food-search-elastic.ts) when the search failed for a REAL reason — a non-2xx
 * answer, a 200 carrying an error, a parse failure — and that failure has ALREADY
 * been reported to the error tracker by the search client itself.
 *
 * Its whole job is to tell a caller "do not report this again". Before it existed,
 * the client reported the failure and then rethrew the bare error, so every
 * caller's own catch reported the same failure a second time under a different
 * name — two Sentry events with different fingerprints for one cause, which nothing
 * pairs up and which doubles every count of how often search fails.
 *
 * Callers still show their own failure state; they only skip the report. This is
 * the same rule, for the same reason, as `DefaultFoodsUnavailableError` on the
 * default-foods read.
 *
 * A caller must NOT simply stop reporting instead of testing for this type. Some
 * failures leave `runElasticQueryAsync` without being reported — a non-timeout
 * rejection of the sign-in wait is thrown before the reporting catch is reached —
 * and those must still be reported by whoever catches them.
 */
export class ReportedSearchFailureError extends Error {
  readonly originalError: unknown;

  constructor(originalError: unknown) {
    const message = describeError(originalError);
    super(`Food search failed (already reported): ${message}`);
    this.name = 'ReportedSearchFailureError';
    this.originalError = originalError;
  }
}

/** How far a food-search request had got when its deadline ran out (#5320). */
export enum SearchRequestPhase {
  /** getting the Firebase token — nothing has been sent yet */
  Token = 'token',
  /** the request was sent and no answer has come back */
  Request = 'request',
  /** the answer started arriving but its body never finished */
  Body = 'body',
}

/**
 * Thrown by the Elasticsearch food search (`runElasticQueryAsync` in food-search-elastic.ts)
 * when the server did not answer within the search's own deadline (#5320).
 *
 * It is deliberately NOT an AbortError and NOT a SearchConnectionError. A slow server is a
 * different signal from a dropped connection, so `classifySearchFailure` puts it in `Other`,
 * exactly where the operating system's own timeout already lands: it is reported, and the Add
 * Foods page shows its "server problem" card.
 *
 * Two exceptions, both classified `Transient`:
 * - (#6391) a deadline that ran out while still getting the sign-in token, with the app already
 *   offline (`deviceLikelyOnline` false). Nothing was sent to our server.
 * - (df-bac7bf9dc8fa) a deadline that ran out before any answer arrived (token or request phase)
 *   when the connection check run at that moment also failed (`connectionCheckPassed` false).
 *   The phone could not reach our server at all, so its network failed, not the search.
 *
 * `elapsedMs` is how long really passed, which is not always the deadline. JavaScript timers
 * stop while an iOS app is in the background and fire late on return, so an elapsed time far
 * above the deadline means the user left the app mid-search.
 */
export class SearchTimeoutError extends Error {
  readonly timeoutMs: number;
  readonly elapsedMs: number;
  readonly phase: SearchRequestPhase;
  /** what the app's connection status said at the moment the deadline ran out (#6391) */
  readonly deviceLikelyOnline: boolean;
  /**
   * whether our server answered the connection check run at the deadline; undefined when no check
   * ran (a body stall, or a token stall already known offline) — see needsStallConnectionCheck
   */
  readonly connectionCheckPassed: boolean | undefined;

  constructor(timeoutMs: number, elapsedMs: number, phase: SearchRequestPhase, deviceLikelyOnline: boolean, connectionCheckPassed?: boolean) {
    super(`Food search got no answer within ${timeoutMs} ms (${elapsedMs} ms passed, stopped at phase "${phase}", ${deviceLikelyOnline ? 'online' : 'offline'}${connectionCheckPassed === undefined ? '' : `, connection check ${connectionCheckPassed ? 'passed' : 'failed'}`})`);
    this.name = 'SearchTimeoutError';
    this.timeoutMs = timeoutMs;
    this.elapsedMs = elapsedMs;
    this.phase = phase;
    this.deviceLikelyOnline = deviceLikelyOnline;
    this.connectionCheckPassed = connectionCheckPassed;
  }
}

/** whether a stall stopped before any answer started arriving, so nothing proves the network worked */
function stoppedBeforeAnyAnswer(phase: SearchRequestPhase): boolean {
  return phase === SearchRequestPhase.Token || phase === SearchRequestPhase.Request;
}

/**
 * Whether a search whose deadline ran out should run the connection check before it is classified
 * (df-bac7bf9dc8fa). Only a stall before any answer arrived needs it. A body stall proves the
 * network carried the start of an answer, and a token stall on a phone already known offline is
 * settled as a connection problem without one (#6391).
 */
export function needsStallConnectionCheck(phase: SearchRequestPhase, deviceLikelyOnline: boolean): boolean {
  if (!stoppedBeforeAnyAnswer(phase)) return false;
  return !(phase === SearchRequestPhase.Token && !deviceLikelyOnline);
}

/**
 * The name and context a genuine search failure is reported under.
 *
 * Our own deadline gets a name of its own, so the number of searches that stalled can be read
 * apart from the number the server actually failed — mixed under one name, a stall storm would
 * hide a burst of real HTTP 500s and the other way round.
 */
export function describeSearchFailureForReport(error: unknown): { name: string; context: Record<string, unknown> } {
  if (error instanceof SearchTimeoutError) {
    return {
      name: 'Food search timed out on the client',
      context: { timeoutMs: error.timeoutMs, elapsedMs: error.elapsedMs, phase: error.phase, deviceLikelyOnline: error.deviceLikelyOnline, connectionCheckPassed: error.connectionCheckPassed },
    };
  }
  return { name: 'Food search backend failed', context: { error: describeErrorForLog(error) } };
}

/** How a failed food search should be interpreted. */
export enum SearchFailureOutcome {
  /** Network blip / iOS "Load failed" / connectivity loss / Firebase auth network
   * failure during token refresh — the request did not reach the server. Show a
   * connection-error state and let the user retry; do NOT report to Sentry. */
  Transient = 'transient',
  /** Anything else — a real server failure (non-2xx, parse error). Surface for
   * telemetry and treat as a genuine failure, not as "no results". */
  Other = 'other',
}

/** Classify a food-search error. Mirrors the transient-vs-real split that the
 * `runElasticQueryAsync` catch already used (`isTransientFetchError` plus the
 * Firebase `auth/network-request-failed` case raised by a token refresh that
 * hit a network error), plus the #4405 backend-route wait below. */
export function classifySearchFailure(error: unknown): SearchFailureOutcome {
  if (isTransientFetchError(error) || isFirebaseAuthNetworkError(error)) {
    return SearchFailureOutcome.Transient;
  }
  // #4405: the search awaits getApiOriginAsync() before it can build a URL, and
  // that wait gives up with this error when the native backend-route probe has
  // not answered. The only thing that delays the probe is the network, so this
  // is a connectivity failure like any other above — the request never reached
  // the server. Without this arm it would fall through to Other, which reports
  // to Sentry and tells the user the search itself failed.
  if (error instanceof BackendRouteUnresolvedError) {
    return SearchFailureOutcome.Transient;
  }
  // #6391: our own deadline ran out while still getting the sign-in token, and the app already
  // knew it was offline. On a phone with no connection that token request can hang instead of
  // failing fast; nothing was sent to our server, so this is a connection problem, not the slow
  // server the deadline exists to catch. A token stall while the app believes it is online, and
  // every request or body stall, stays Other (#5320).
  if (error instanceof SearchTimeoutError && error.phase === SearchRequestPhase.Token && !error.deviceLikelyOnline) {
    return SearchFailureOutcome.Transient;
  }
  // df-bac7bf9dc8fa: the deadline ran out before any answer arrived and, at that moment, the phone
  // could not reach our server's tiny static file either. Its network failed while the app still
  // believed it was online, so this is a connection problem too. A passed check (or none) stays Other:
  // the phone could reach us, so the search itself was slow.
  if (error instanceof SearchTimeoutError && stoppedBeforeAnyAnswer(error.phase) && error.connectionCheckPassed === false) {
    return SearchFailureOutcome.Transient;
  }
  return SearchFailureOutcome.Other;
}

/**
 * Whether a failed food search stopped on a read that could not run for a passing reason, which
 * is filed as info under its own name rather than as `FoodSearchFailed` (#6350). The user sees
 * the same failure card either way; only the report changes.
 *
 * Passing means one of three things: our own sign-out shutting the database down, a database
 * recovery cutting the read short, or a network failure that the next try usually survives.
 * Every other refusal stays a problem. `NoDatabaseHandle` in particular can be a failed start-up
 * or a destroyed store, and `isDatabaseShuttingDown` explains why quietening it would hide one.
 */
export function isPassingSearchReadFailure(error: unknown): boolean {
  return isDatabaseShuttingDown(error) || isDatabaseRecovering(error) || isOfflineOrTransientNetworkError(error);
}
